import { useTranslation } from "react-i18next"
import { Button, Group, Select, TextInput, Stack } from "@mantine/core"
import { useForm } from "@mantine/form"
import type { ContactAlternativeFormValues, ContactAlternative } from "../models/contact"
import { useCreateAlternativeContact, useUpdateAlternativeContact, useContactTypes } from "../hooks/useContacts"

interface ContactAlternativeFormProps {
  contactId: number
  editItem?: ContactAlternative | null
  onSuccess: () => void
  onCancel: () => void
}

export default function ContactAlternativeForm({
  contactId,
  editItem,
  onSuccess,
  onCancel,
}: ContactAlternativeFormProps) {
  const { t } = useTranslation()
  const createAlt = useCreateAlternativeContact()
  const updateAlt = useUpdateAlternativeContact()
  const { data: typesData } = useContactTypes()

  const deptOptions = (typesData?.department_types ?? []).map((d: { id: number; option: string }) => ({
    value: String(d.id),
    label: d.option,
  }))

  const form = useForm<ContactAlternativeFormValues>({
    initialValues: {
      contact_id: contactId,
      type: editItem?.type ?? 0,
      name: editItem?.name ?? null,
      role: editItem?.role ?? null,
      phone_number: editItem?.phone_number ?? null,
      email: editItem?.email ?? null,
    },
  })

  const handleSubmit = (values: ContactAlternativeFormValues) => {
    if (editItem) {
      updateAlt.mutate(
        { id: editItem.id, values },
        { onSuccess },
      )
    } else {
      createAlt.mutate(values, { onSuccess })
    }
  }

  const isPending = createAlt.isPending || updateAlt.isPending

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <Select
          label={t("contacts.altDepartment")}
          placeholder={t("contacts.selectDepartment")}
          data={deptOptions}
          searchable
          value={form.values.type ? String(form.values.type) : null}
          onChange={(val) => form.setFieldValue("type", val ? Number(val) : 0)}
        />
        <TextInput
          label={t("contacts.altName")}
          placeholder={t("contacts.altName")}
          {...form.getInputProps("name")}
        />
        <TextInput
          label={t("contacts.altRole")}
          placeholder={t("contacts.altRole")}
          {...form.getInputProps("role")}
        />
        <TextInput
          label={t("contacts.altPhone")}
          placeholder={t("contacts.altPhone")}
          {...form.getInputProps("phone_number")}
        />
        <TextInput
          label={t("contacts.altEmail")}
          placeholder={t("contacts.altEmail")}
          {...form.getInputProps("email")}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={isPending}>
            {editItem ? t("common.update") : t("common.create")}
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
